import * as React from 'react';
import {assignTokens, toIntent} from '@sail/engine';
import type {Intent} from '@sail/engine';
import {tokens} from '@sail/tokens';
import {messenger} from './messenger';

/**
 * Subscribes to `SAIL_TOKEN_OVERRIDE` messages from the Sail Toolkit
 * extension and returns an `Intent` that applies them to the active token
 * tree via `assignTokens`.
 *
 * The subscription is only set up when the Sail Toolkit extension is active.
 * Before that, no listener is registered and the returned intent is
 * a no-op.
 *
 * @param active - Whether the Sail Toolkit extension is active.
 * @returns An `Intent` that applies the latest token overrides, or a no-op
 *   intent when no overrides have been received.
 * @internal
 */
export function useTokensOverrides(active: boolean): Intent {
  const [overrides, setOverrides] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!active) {
      return;
    }

    const unsubscribe = messenger?.onMessageType(
      'SAIL_TOKEN_OVERRIDE',
      (data) => {
        setOverrides(data);
      },
    );
    return () => unsubscribe?.();
  }, [active]);

  return React.useMemo(
    () =>
      Object.keys(overrides).length > 0
        ? assignTokens(tokens, overrides)
        : toIntent([]),
    [overrides],
  );
}
